import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ActionDialog } from "@/components/board/ActionDialog";
import { useAuth } from "@/hooks/useAuth";
import { useRowAction } from "@/hooks/useRowAction";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { useBoardPeople } from "@/hooks/useBoardPeople";
import { showToast } from "@/lib/toast";
import { roleOf } from "@/lib/statusRoles";
import { defaultActionRecipients } from "@/lib/actionRecipients";
import { t } from "@/lib/strings";
import type { RowActionType, UUID } from "@/lib/types";
import { isWorkflowBoard } from "@/lib/boardView";
import type { ViewBoard, ViewRow } from "@/lib/boardView";

interface Props {
  row: ViewRow;
  board: ViewBoard;
}

export function RowActionBar({ row, board }: Props) {
  const { user } = useAuth();
  const { data: members = [] } = useWorkspaceMembers(board.workspace_id as UUID);
  // Board people = workspace members ∪ cross-dept guests. The recipient name
  // pool must include guests so the ticket creator (often from another Bereich,
  // hence only a guest here) resolves to a real name/avatar instead of a "—".
  const { data: boardPeople = [] } = useBoardPeople(board.id as UUID);
  const rawRow = row.raw;
  const action = useRowAction(rawRow?.id as UUID | undefined, board.id as UUID);

  const [openAction, setOpenAction] = useState<RowActionType | null>(null);

  // Members API returns internal UUIDs; useAuth gives the keycloak sub + email.
  // Filter the actor out by email — robust because backend User.email is the
  // same as the JWT email claim. Falls through if email is missing.
  const otherMembers = useMemo(
    () => members.filter((m) => !user?.email || m.email !== user.email),
    [members, user?.email],
  );
  // Name/avatar pool + "add more" list for the notify picker — board people
  // (members + guests) minus the actor, so a cross-dept creator resolves.
  const recipientPool = useMemo(
    () => boardPeople.filter((p) => !user?.email || p.email !== user.email),
    [boardPeople, user?.email],
  );
  // The viewer's own membership — staff-ness is a workspace WRITE role, NOT
  // "did you create this". So an owner who filed a ticket still gets the staff
  // actions (incl. Lösen) on their own ticket.
  const me = useMemo(
    () => members.find((m) => Boolean(user?.email) && m.email === user?.email),
    [members, user?.email],
  );
  const isStaff = me?.role === "owner" || me?.role === "admin" || me?.role === "member";
  const isAdmin = me?.role === "owner" || me?.role === "admin";
  // Annehmen assignee options: owners/admins assign anyone in the department,
  // members can only assign themselves.
  const assigneeOptions = isAdmin ? members : me ? [me] : [];

  if (!isWorkflowBoard(board)) return null;
  const isIdeas = board.kind === "ideas";
  const statusCol = board.columns.find((c) => c.type === "status");
  if (!statusCol) return null;
  if (!rawRow) return null;

  const viewerIsCreator =
    Boolean(user?.sub) &&
    rawRow.created_by_keycloak_sub != null &&
    user?.sub === rawRow.created_by_keycloak_sub;

  // A viewer-member Gutachter (assigned in this idea's person cell) is
  // backend-authorized (Task 5) to accept/reject even though `isStaff` excludes
  // viewer. Read the person cell the same way boardFilters/boardSort do: value
  // is a list of user-id strings; degrade safely to [] if missing.
  const personCol = board.columns.find((c) => c.type === "person");
  const gutachterVal = personCol
    ? rawRow.cells?.find((c) => c.column_def_id === personCol.id)?.value
    : null;
  const gutachterIds = Array.isArray(gutachterVal) ? (gutachterVal as string[]) : [];
  const isGutachter = Boolean(me?.user_id) && gutachterIds.includes(me!.user_id);

  const currentStatusOpt = statusCol.options?.find((o) => o.id === row.status);
  const currentRole = roleOf(currentStatusOpt);
  const isClosed = currentRole === "closed";
  // Only offer a transition whose target option actually exists on this board.
  // The live IT board only gains gesichtet/eingeplant/in-review/zurueckgestellt
  // once the one-off migration runs, so this keeps the buttons graceful (no
  // 422) regardless of deploy-vs-migration ordering.
  const optionIds = new Set((statusCol.options ?? []).map((o) => o.id));

  // Closed outcomes (Gelöst / Nicht relevant) the resolve dialog offers as a
  // picker when there is more than one.
  const closedOutcomes = (statusCol.options ?? [])
    .filter((o) => roleOf(o) === "closed")
    .map((o) => ({ id: o.id, label: o.label, color: o.color as string }));

  function defaultRecipientsFor(a: RowActionType): UUID[] {
    if (!rawRow) return [];
    // Pure decision (lib/actionRecipients) — Annehmen / Senden zur Prüfung / Lösen
    // all pre-select the ticket CREATOR, so the requester stays in the loop.
    return defaultActionRecipients(a, {
      createdBy: (rawRow.created_by as UUID | null) ?? null,
      lastActorUserId: (rawRow.last_actor_user_id as UUID | null) ?? null,
      ownerIds: members.filter((m) => m.role === "owner").map((m) => m.user_id),
      otherMemberIds: otherMembers.map((m) => m.user_id),
      meId: me?.user_id ?? null,
    });
  }

  function run(a: RowActionType) {
    setOpenAction(a);
  }

  function submit(payload: {
    body: string;
    recipient_user_ids: UUID[];
    target_option_id?: string;
    assignee_user_ids?: UUID[];
  }) {
    if (!openAction) return;
    action.mutate(
      { action: openAction, ...payload },
      {
        onSuccess: () => setOpenAction(null),
        onError: (e) => {
          const status = (e as { status?: number }).status;
          if (status === 422) showToast(t.actionStatusColumnMissing, "error");
          else showToast(t.actionSubmitFailed, "error");
        },
      },
    );
  }

  const showStaff = isStaff;
  const showCreator = viewerIsCreator && isClosed;
  // The creator can withdraw their OWN still-open ticket as obsolete (→ "Nicht
  // relevant"), only when that closed outcome exists on the board.
  const showCreatorWithdraw = viewerIsCreator && !isClosed && optionIds.has("nicht-relevant");
  // On an ideas board the ticket verbs are hidden; the bar stays visible for
  // staff (all three buttons) AND for a viewer-member Gutachter (the two
  // decision buttons) so the idea-decision branch below can render.
  if (!showStaff && !showCreator && !showCreatorWithdraw && !(isIdeas && (isStaff || isGutachter)))
    return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        padding: "10px 14px",
        margin: "16px 0",
        borderRadius: 10,
        background: "var(--surface-soft)",
        border: "1px solid var(--hairline-soft)",
      }}
    >
      {isIdeas && (isStaff || isGutachter) ? (
        <>
          {/* Assignment ("Zur Bewertung zuweisen") stays staff-only. */}
          {isStaff ? (
            <Button variant="secondary" size="sm" onClick={() => run("zuweisen")}>
              {t.actionIdeaAssign}
            </Button>
          ) : null}
          {/* Decision buttons: staff OR the assigned Gutachter (even if viewer). */}
          <Button variant="secondary" size="sm" onClick={() => run("annehmen_idee")}>
            {t.actionIdeaAccept}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => run("ablehnen_idee")}>
            {t.actionIdeaReject}
          </Button>
        </>
      ) : null}
      {!isIdeas && showStaff ? (
        <>
          <Button variant="secondary" size="sm" onClick={() => run("annehmen")}>
            {t.actionClaim}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => run("senden_zur_pruefung")}>
            {t.actionSendForReview}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => run("wartet_auf_kunde")}>
            {t.actionWaitingForCustomer}
          </Button>
          {/* secondary (not primary): these are transition verbs, not states —
              a filled button reads as "currently selected", which confused a
              brand-new Offen ticket into looking resolved. */}
          <Button variant="secondary" size="sm" onClick={() => run("loesen")}>
            {t.actionResolve}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => run("zuweisen")}>
            {t.actionAssign}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => run("eskalieren")}>
            {t.actionEscalate}
          </Button>
          {currentRole === "open" && row.status === "offen" && optionIds.has("gesichtet") ? (
            <Button variant="secondary" size="sm" onClick={() => run("sichten")}>
              {t.actionSichten}
            </Button>
          ) : null}
          {currentRole === "open" && optionIds.has("eingeplant") ? (
            <Button variant="secondary" size="sm" onClick={() => run("einplanen")}>
              {t.actionEinplanen}
            </Button>
          ) : null}
          {currentRole === "in_progress" && optionIds.has("in-review") ? (
            <Button variant="secondary" size="sm" onClick={() => run("in_review")}>
              {t.actionInReview}
            </Button>
          ) : null}
          {(currentRole === "in_progress" || currentRole === "waiting") &&
          optionIds.has("zurueckgestellt") ? (
            <Button variant="secondary" size="sm" onClick={() => run("zurueckstellen")}>
              {t.actionZurueckstellen}
            </Button>
          ) : null}
        </>
      ) : null}
      {!isIdeas && showCreator ? (
        <>
          <Button variant="primary" size="sm" onClick={() => run("bestaetigen")}>
            {t.actionConfirm}
          </Button>
          <Button variant="destructive" size="sm" onClick={() => run("erneut_oeffnen")}>
            {t.actionReopen}
          </Button>
        </>
      ) : null}
      {!isIdeas && showCreatorWithdraw ? (
        <Button variant="destructive" size="sm" onClick={() => run("hinfaellig")}>
          {t.actionHinfaellig}
        </Button>
      ) : null}

      <ActionDialog
        open={openAction !== null}
        onOpenChange={(o) => !o && setOpenAction(null)}
        action={openAction}
        defaultRecipients={openAction ? defaultRecipientsFor(openAction) : []}
        candidateMembers={recipientPool}
        busy={action.isPending}
        onSubmit={submit}
        outcomeOptions={isIdeas ? [] : closedOutcomes}
        assigneeOptions={assigneeOptions}
        defaultAssigneeId={me?.user_id ?? null}
      />
    </div>
  );
}
